import { ReactNode, useEffect, useState } from 'react';

export type DeviceType = 'phone' | 'tablet' | 'desktop' | 'largeDesktop' | 'tv';

export type DeviceWidthBounds = [number, number];

export type DeviceTypeBounds = [DeviceType | null, DeviceType | null];

export interface DeviceFragmentProps {
    children?: ReactNode;
    type: DeviceType | DeviceTypeBounds;
}

export const deviceTypes: DeviceType[] = ['phone', 'tablet', 'desktop', 'largeDesktop', 'tv'];

export const breakpoints: Record<DeviceType, DeviceWidthBounds> = {
    phone: [-Infinity, 480],
    tablet: [481, 768],
    desktop: [769, 1024],
    largeDesktop: [1025, 1440],
    tv: [1441, Infinity],
};

export default function DeviceFragment({ children, type }: DeviceFragmentProps) {
    const [device, setDevice] = useState(getDeviceType());

    useEffect(() => {
        let responsiveLock = false;

        function updateDevice() {
            if (responsiveLock) return;
            responsiveLock = true;
            setDevice(getDeviceType());
            setTimeout(() => void (responsiveLock = false), 100);
        }

        window.addEventListener('resize', updateDevice);

        return () => window.removeEventListener('resize', updateDevice);
    }, []);

    if (Array.isArray(type)) {
        let [minDevice, maxDevice] = type;

        if (minDevice === null) minDevice = deviceTypes[0];
        if (maxDevice === null) maxDevice = deviceTypes[deviceTypes.length - 1];

        if (
            deviceTypes.indexOf(device) >= deviceTypes.indexOf(minDevice as DeviceType) &&
            deviceTypes.indexOf(device) <= deviceTypes.indexOf(maxDevice as DeviceType)
        ) {
            return <>{children}</>;
        }
    }
    if (device === type) return <>{children}</>;

    return <></>;
}

function checkBounds(width: number, device: DeviceType): boolean {
    const [min, max] = breakpoints[device];
    return width >= min && width <= max;
}

function getDeviceType(): DeviceType {
    const width = window.innerWidth;

    for (const device in breakpoints) {
        if (checkBounds(width, device as DeviceType)) return device as DeviceType;
    }

    throw new Error(
        `Could not find device for specified width: ${width}. This is a problem with DeviceFragment.tsx.`,
    );
}
